import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { FormControlErrorPipe, FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';

@Component({
  selector: 'markant-platform-group-name-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    ModalBridgeComponent,
    ReactiveFormsModule,
    TranslocoModule,
    FormControlErrorPipe,
    FormControlKeyErrorPipe,
  ],
  templateUrl: './group-name-edit-dialog.component.html',
  styleUrls: ['./group-name-edit-dialog.component.scss'],
})
export class GroupNameEditDialogComponent implements OnInit, OnChanges {
  @Input({ required: true }) groupName!: string;
  @Input({ required: true }) groupId!: string;

  @Output() groupNameUpdated = new EventEmitter<string>();

  isOpen = false;
  groupNameInput!: FormControl;

  isLoading = false;

  constructor(
    private authorizerService: AuthorizerService,
    private snackbarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.groupNameInput = new FormControl(this.groupName, [Validators.required, Validators.maxLength(50)]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['groupName'] &&
      this.groupNameInput &&
      this.groupNameInput.value !== changes['groupName'].currentValue
    ) {
      this.groupNameInput.setValue(this.groupName);
    }
  }

  async validateAndRenameGroup(): Promise<void> {
    this.groupNameInput.updateValueAndValidity();

    if (this.groupName === this.groupNameInput.value) {
      this.isOpen = false;
      return;
    }

    if (!this.groupNameInput.valid) {
      return;
    }

    try {
      this.isLoading = true;
      const group = await firstValueFrom(this.authorizerService.getGroupById(this.groupId));
      const updatedGroup = await firstValueFrom(
        this.authorizerService.updateGroupName(group.id, this.groupNameInput.value, group.version)
      );

      this.isOpen = false;
      this.isLoading = false;
      this.groupNameUpdated.emit(updatedGroup.displayName);
      await this.updateBreadcrumb();
      this.snackbarService.showSuccessSnackbarWithTransloco(
        'dashboardOverview.groupAssignment.groupNameEditDialog.successMessage'
      );
    } catch (e) {
      this.isOpen = false;
      this.isLoading = false;
      this.snackbarService.showErrorSnackbarWithTransloco('error.common');
      this.groupNameInput.setValue(this.groupName);

      throw e;
    }
  }

  resetAndClose(): void {
    this.isOpen = false;
    this.groupNameInput.setValue(this.groupName);
  }

  private async updateBreadcrumb(): Promise<void> {
    // Navigate to current URL to update group name in Breadcrumb.
    this.router.onSameUrlNavigation = 'reload';

    await this.router.navigate(['./'], { relativeTo: this.route });

    this.router.onSameUrlNavigation = 'ignore';
  }
}
